import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import type { SlotInfo } from 'react-big-calendar';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loading } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './Events.module.scss';

import 'moment/locale/pl';
import AddEventHandlerContainer from 'Screens/Dashboard/Events/AddEventHandler/AddEventHandler';
import EditEventHandlerContainer from 'Screens/Dashboard/Events/EditEventHandler/EditEventHandler';
import { useGetEventsByEntityQuery } from 'api/eventEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import {
    EventDto, HowLongDto, HowLongMode, RecurrenceType, SimpleEvent,
} from 'api/dtos/event';
import sharedMessages from 'Components/utils/sharedMessages';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import { useGetCategoriesByEntityQuery } from 'api/categoryEndpoints';
import { getAccessibleTagColors } from 'utils/colorUtils';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import useGetUserPermission from 'hooks/useGetUserPermission';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import useGetUser from 'hooks/useGetUser';
import CalendarToolbar, { CalendarToolbarDisabledContext } from './CalendarToolbar/CalendarToolbar';
import UpcomingEventsPanel from './UpcomingEventsPanel/UpcomingEventsPanel';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

const localizer = momentLocalizer(moment);

const intlMessages = defineMessages({
    addEvent: {
        id: 'src.Screens.Dashboard.Events.Events.addEvent',
        defaultMessage: 'Dodaj wydarzenie',
    },
    allDay: {
        id: 'src.Screens.Dashboard.Events.Events.allDay',
        defaultMessage: 'Cały dzień',
    },
    previous: {
        id: 'src.Screens.Dashboard.Events.Events.previous',
        defaultMessage: 'Poprzedni',
    },
    next: {
        id: 'src.Screens.Dashboard.Events.Events.next',
        defaultMessage: 'Następny',
    },
    today: {
        id: 'src.Screens.Dashboard.Events.Events.today',
        defaultMessage: 'Dzisiaj',
    },
    month: {
        id: 'src.Screens.Dashboard.Events.Events.month',
        defaultMessage: 'Miesiąc',
    },
    week: {
        id: 'src.Screens.Dashboard.Events.Events.week',
        defaultMessage: 'Tydzień',
    },
    day: {
        id: 'src.Screens.Dashboard.Events.Events.day',
        defaultMessage: 'Dzień',
    },
    agenda: {
        id: 'src.Screens.Dashboard.Events.Events.agenda',
        defaultMessage: 'Agenda',
    },
    date: {
        id: 'src.Screens.Dashboard.Events.Events.date',
        defaultMessage: 'Data',
    },
    time: {
        id: 'src.Screens.Dashboard.Events.Events.time',
        defaultMessage: 'Czas',
    },
    event: {
        id: 'src.Screens.Dashboard.Events.Events.event',
        defaultMessage: 'Wydarzenie',
    },
    showMore: {
        id: 'src.Screens.Dashboard.Events.Events.showMore',
        defaultMessage: '+{count} więcej',
    },
    addEventDisabledTooltip: {
        id: 'src.Screens.Dashboard.Events.Events.addEventDisabledTooltip',
        defaultMessage: 'Nie masz odpowiednich uprawnień w tej grupie albo nie masz wykupionej odpowiedniej subskrypcji koniecznej do dodania wydarzenia.',
    },
});

const formatMonthStart = (date: Date) => moment(date).startOf('month').format('YYYY-MM-DD');
const formatMonthPlusThreeEnd = (date: Date) => moment(date).add(3, 'months').endOf('month').format('YYYY-MM-DD');

const getCalendarEventEnd = (event: EventDto) => {
    const start = moment(event.dateFrom);
    const end = moment(event.dateTo);

    if (event.allDay && !end.isAfter(start)) {
        return start.clone().add(1, 'day').toDate();
    }

    return end.toDate();
};

const MonthEvent = ({ event }: { event: SimpleEvent }) => (
    <span>
        {event.allDay ? event.title : `${moment(event.start).format('HH:mm')} ${event.title}`}
    </span>
);

const Events = () => {
    const intl = useIntl();
    const [selectedEvent, setSelectedEvent] = useState<EventDto>();
    const [createEventOpen, setCreateEventOpen] = useState(false);
    const [createEventInitialDate, setCreateEventInitialDate] = useState<string>();
    const [rangeStart, setRangeStart] = useState<string>(formatMonthStart(new Date()));
    const [rangeEnd, setRangeEnd] = useState<string>(formatMonthPlusThreeEnd(new Date()));
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState<View>('month');
    const currentEntityId = useGetCurrentEntityId();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();
    const hasPermission = isAdmin || isModerator || isOwner;
    const {
        activeSubscription,
        isFetching: isFetchingSubscription,
        isPaidPlan,
    } = useGetCurrentSubscription();
    const hasSubscriptionAccess = !isFetchingSubscription && isPaidPlan && activeSubscription?.hasAccess !== false;
    const isTopBarActionDisabled = !hasPermission || !hasSubscriptionAccess;
    const {
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    } = useMainContainer();
    const calendarMessages = useMemo(() => ({
        allDay: intl.formatMessage(intlMessages.allDay),
        previous: intl.formatMessage(intlMessages.previous),
        next: intl.formatMessage(intlMessages.next),
        today: intl.formatMessage(intlMessages.today),
        month: intl.formatMessage(intlMessages.month),
        week: intl.formatMessage(intlMessages.week),
        day: intl.formatMessage(intlMessages.day),
        agenda: intl.formatMessage(intlMessages.agenda),
        date: intl.formatMessage(intlMessages.date),
        time: intl.formatMessage(intlMessages.time),
        event: intl.formatMessage(intlMessages.event),
        showMore: (count: number) => intl.formatMessage(intlMessages.showMore, { count }),
    }), [intl]);

    const {
        data, isLoading, isFetching, isError, refetch,
    } = useGetEventsByEntityQuery({
        entityId: currentEntityId ?? '',
        rangeStart,
        rangeEnd,
    }, {
        skip: !currentEntityId,
    });

    const events = useMemo(() => data ?? [], [data]);

    const {
        data: categoriesData,
        isLoading: loadingCategories,
        isError: errorCategories,
        refetch: refetchCategories,
    } = useGetCategoriesByEntityQuery(currentEntityId ?? '', {
        skip: !currentEntityId,
    });

    const categories = useMemo(() => new Map((categoriesData ?? []).map((cat) => [cat.id, cat])), [categoriesData]);

    const allEvents = useMemo<SimpleEvent[]>(() => events.map((event) => ({
        id: event.occurenceId,
        occurenceId: event.occurenceId,
        title: event.title,
        allDay: event.allDay,
        start: new Date(event.dateFrom),
        end: getCalendarEventEnd(event),
        desc: event.description,
        attendees: event.attendees,
        categoryIds: event.categoryIds,
        color: event.color,
    })), [events]);

    const upcomingEvents = useMemo(() => events
        .filter((event) => moment(event.dateTo).isSameOrAfter(moment().startOf('day')))
        .sort((a, b) => new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime()), [events]);

    const eventsMap = useMemo(() => new Map(events.map((event) => [event.occurenceId, event])), [events]);

    const openCreateEvent = useCallback(() => {
        setCreateEventInitialDate(undefined);
        setCreateEventOpen(true);
    }, []);

    useEffect(() => {
        setTopBarAction(() => openCreateEvent);
        setTopBarActionTitle(intl.formatMessage(intlMessages.addEvent));
        setTopBarIcon(<Add size={20} />);
        setDisabledAction(isTopBarActionDisabled);
        setDisabledActionTooltip(intl.formatMessage(intlMessages.addEventDisabledTooltip));

        return () => {
            setTopBarAction(null);
            setTopBarActionTitle(undefined);
            setTopBarIcon(undefined);
            setDisabledAction(false);
            setDisabledActionTooltip(undefined);
        };
    }, [
        intl,
        isTopBarActionDisabled,
        openCreateEvent,
        setDisabledAction,
        setDisabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
    ]);

    const onNavigate = useCallback((date: Date) => {
        setCalendarDate(date);

        const requiredStart = formatMonthStart(date);
        const requiredEnd = formatMonthPlusThreeEnd(date);

        if (moment(requiredStart).isBefore(rangeStart, 'day')) {
            setRangeStart(requiredStart);
        }

        if (moment(requiredEnd).isAfter(rangeEnd, 'day')) {
            setRangeEnd(requiredEnd);
        }
    }, [rangeEnd, rangeStart]);

    const onView = useCallback((view: View) => {
        setCalendarView(view);
    }, []);

    const onSelectSlot = useCallback(({ action, start }: SlotInfo) => {
        if (action !== 'doubleClick' || isTopBarActionDisabled) {
            return;
        }

        setCreateEventInitialDate(moment(start).format('YYYY-MM-DD'));
        setCreateEventOpen(true);
    }, [isTopBarActionDisabled]);

    const valueToContext = useMemo(() => {
        if (!selectedEvent) {
            return null;
        }

        const recurrenceEndDate = selectedEvent.recurrenceEndDate
            ? moment(selectedEvent.recurrenceEndDate).format('YYYY-MM-DD')
            : undefined;
        const recurrence = selectedEvent.recurrence ?? {
            type: RecurrenceType.None,
        };
        const hasConfigurableRecurrenceEnd = recurrence.type !== RecurrenceType.None
            && recurrence.type !== RecurrenceType.Yearly;
        let howLong: HowLongDto | undefined;

        if (hasConfigurableRecurrenceEnd) {
            howLong = recurrenceEndDate
                ? { mode: HowLongMode.UntilDate, untilDate: recurrenceEndDate }
                : selectedEvent.howLong ?? { mode: HowLongMode.Always };
        }

        return {
            ...selectedEvent,
            recurrenceEndDate,
            isEdit: true,
            timeFrom: moment(selectedEvent.dateFrom).format('HH:mm'),
            timeTo: moment(selectedEvent.dateTo).format('HH:mm'),
            isMultiDay: !moment(selectedEvent.dateFrom).isSame(moment(selectedEvent.dateTo), 'day'),
            recurrence,
            howLong,
            optionalPointsPerEvent: selectedEvent.optionalPointsPerEvent,
            absencePenaltyPoints: selectedEvent.absencePenaltyPoints ?? null,
            qrConfirmationWindowMinutes: selectedEvent.qrConfirmationWindowMinutes,
            attendeeReminderMinutesBefore: selectedEvent.attendeeReminderMinutesBefore ?? null,
            kind: selectedEvent.kind ?? (selectedEvent.isGlobal ? 'Global' : 'Local'),
            scope: selectedEvent.scope ?? (selectedEvent.isGlobal ? 'GlobalAllChildren' : 'LocalOnly'),
            targetEntityIds: selectedEvent.targetEntityIds ?? [],
            color: selectedEvent.color ?? '#3174AD',
            priests: selectedEvent.priests ?? [],
            priestIds: selectedEvent.priestIds ?? [],
        };
    }, [selectedEvent]);

    const eventHandler = (
        event: SimpleEvent,
        start: Date,
        end: Date,
        isSelected: boolean,
    ) => {
        const categoryId = event.categoryIds[0];
        const palette = getAccessibleTagColors(event.color || categories.get(categoryId)?.color || '#3174ad');

        return {
            style: {
                backgroundColor: palette.backgroundColor,
                color: palette.textColor,
                borderRadius: '4px',
                border: 'none',
                padding: '4px 8px',
                fontWeight: isSelected ? 'bold' : 'normal',
            },
        };
    };

    const onSelectEvent = useCallback((newlySelectedEvent: SimpleEvent) => {
        const event = eventsMap.get(newlySelectedEvent.occurenceId);
        if (event) {
            setSelectedEvent(event);
        }
    }, [eventsMap]);

    const onCloseEdit = useCallback(() => {
        setSelectedEvent(undefined);
    }, []);

    const onRefetch = useCallback(async () => {
        if (isError) {
            await refetch();
        }

        if (errorCategories) {
            await refetchCategories();
        }
    }, [errorCategories, isError, refetch, refetchCategories]);

    const loadMoreUpcomingEvents = useCallback(() => {
        setRangeEnd((current) => moment(current).add(3, 'months').endOf('month').format('YYYY-MM-DD'));
    }, []);

    return (
        <>
            <div className={styles.container}>
                {(() => {
                    if (isLoading || loadingCategories) {
                        return <Loading />;
                    }

                    if ((isError && events.length === 0) || errorCategories) {
                        return <ErrorActionState className={styles.error} onClick={onRefetch} />;
                    }

                    return (
                        <div className={styles.content}>
                            <CalendarToolbarDisabledContext.Provider value={isLoading}>
                                <Calendar
                                    localizer={localizer}
                                    events={allEvents}
                                    culture="pl"
                                    date={calendarDate}
                                    view={calendarView}
                                    eventPropGetter={eventHandler}
                                    onSelectEvent={onSelectEvent}
                                    onSelectSlot={onSelectSlot}
                                    onNavigate={onNavigate}
                                    onView={onView}
                                    selectable={isTopBarActionDisabled ? false : 'ignoreEvents'}
                                    messages={calendarMessages}
                                    views={['month', 'week', 'day']}
                                    components={{
                                        toolbar: CalendarToolbar,
                                        month: {
                                            event: MonthEvent,
                                        },
                                    }}
                                    startAccessor="start"
                                    endAccessor="end"
                                    className={styles.calendar}
                                />
                            </CalendarToolbarDisabledContext.Provider>
                            {isFetching && (
                                <div className={styles.inlineLoading}>
                                    <Loading small withOverlay={false} />
                                </div>
                            )}
                            <UpcomingEventsPanel
                                events={upcomingEvents}
                                isFetching={isFetching}
                                onEventSelect={setSelectedEvent}
                                onLoadMore={loadMoreUpcomingEvents}
                            />
                        </div>
                    );
                })()}
            </div>
            <AddEventHandlerContainer
                open={createEventOpen}
                initialDate={createEventInitialDate}
                onClose={() => {
                    setCreateEventOpen(false);
                    setCreateEventInitialDate(undefined);
                }}
            />
            {valueToContext && <EditEventHandlerContainer onClose={onCloseEdit} event={valueToContext} />}
        </>
    );
};

const EventsStateHandler = () => {
    const currentEntityId = useGetCurrentEntityId();
    const intl = useIntl();

    const user = useGetUser();
                
    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    if (!currentEntityId) {
        return <EmptyState title={intl.formatMessage(sharedMessages.noEntitySelected)} />;
    }

    return <Events />;
};

export default EventsStateHandler;
